/**
 * Date formatting utilities
 */
import { DateTime } from 'luxon';

/**
 * Checks that a date string is a real YYYY-MM-DD date with a year strictly
 * between 2000 and 2100. Date inputs emit partial values like "0020-11-01"
 * while the year is being typed; those must not reach the API.
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {boolean}
 */
export const isValidAppointmentDate = (dateStr) => {
  const date = DateTime.fromFormat(dateStr || '', 'yyyy-MM-dd');
  return date.isValid && date.year > 2000 && date.year < 2100;
};

/**
 * Formats a date string to human-readable Spanish format
 * @param {string|Date} dateString - The date to format (ISO string, DD-MM-YYYY, DD/MM/YYYY, or Date object)
 * @returns {string} Formatted date in Spanish (e.g., "15 de enero de 2023") or "No especificado" if invalid
 */
export const formatDateEs = (dateString) => {
  if (!dateString) return 'No especificado';

  const monthNames = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ];

  const toHumanEs = (y, m, d) => {
    const mi = parseInt(m, 10);
    const di = parseInt(d, 10);
    if (!mi || !di || mi < 1 || mi > 12) return `${d}-${m}-${y}`;
    return `${di} de ${monthNames[mi - 1]} de ${y}`;
  };

  if (typeof dateString === 'string') {
    // ISO or YYYY-MM-DD (optionally with time) -> use first 3 groups
    const iso = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return toHumanEs(iso[1], iso[2], iso[3]);
    
    // DD-MM-YYYY or DD/MM/YYYY
    const dmy = dateString.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
    if (dmy) return toHumanEs(dmy[3], dmy[2], dmy[1]);
    
    // Otherwise, return as-is
    return dateString;
  }

  // If a Date object is provided (unlikely from API), format without locale conversion assumptions
  if (dateString instanceof Date && !isNaN(dateString.getTime())) {
    const y = String(dateString.getFullYear());
    const m = String(dateString.getMonth() + 1).padStart(2, '0');
    const d = String(dateString.getDate()).padStart(2, '0');
    return toHumanEs(y, m, d);
  }

  return 'No especificado';
};

/**
 * Formats a date string to YYYY-MM-DD format for HTML input fields
 * @param {string|Date} dateString - The date to format (ISO string, DD-MM-YYYY, DD/MM/YYYY, or Date object)
 * @returns {string} Formatted date in YYYY-MM-DD format or empty string if invalid
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';

  try {
    let date;
    
    if (typeof dateString === 'string') {
      // Try ISO format first
      const iso = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) {
        date = new Date(iso[1], parseInt(iso[2]) - 1, parseInt(iso[3]));
      } else {
        // Try DD-MM-YYYY or DD/MM/YYYY
        const dmy = dateString.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
        if (dmy) {
          date = new Date(parseInt(dmy[3]), parseInt(dmy[2]) - 1, parseInt(dmy[1]));
        } else {
          date = new Date(dateString);
        }
      }
    } else if (dateString instanceof Date) {
      date = dateString;
    } else {
      return '';
    }

    if (isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    return '';
  }
};

/**
 * Calculates age from a date of birth
 * @param {string|Date} dateOfBirth - The date of birth to calculate age from
 * @returns {string} Age in format "X años" or "No especificado" if invalid
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return 'No especificado';

  try {
    let birthDate;
    
    // Handle different date formats
    if (typeof dateOfBirth === 'string') {
      // Try ISO format first
      const iso = dateOfBirth.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) {
        birthDate = new Date(iso[1], parseInt(iso[2]) - 1, parseInt(iso[3]));
      } else {
        // Try DD-MM-YYYY or DD/MM/YYYY
        const dmy = dateOfBirth.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
        if (dmy) {
          birthDate = new Date(parseInt(dmy[3]), parseInt(dmy[2]) - 1, parseInt(dmy[1]));
        } else {
          birthDate = new Date(dateOfBirth);
        }
      }
    } else if (dateOfBirth instanceof Date) {
      birthDate = dateOfBirth;
    } else {
      return 'No especificado';
    }

    if (isNaN(birthDate.getTime())) {
      return 'No especificado';
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 0 ? `${age} años` : 'No especificado';
  } catch (error) {
    return 'No especificado';
  }
};

export const getDateString = (date, timezone) => {
  return DateTime.fromJSDate(date).setZone(timezone).toISO().split('T')[0];
};

export const getTimeString = (date, timezone) => {
  return DateTime.fromJSDate(date).setZone(timezone).toFormat('HH:mm');
}

export const getTodayDateString = (timezone) => {
  const userTimezone = timezone || "America/Mexico_City";
  return DateTime.now().setZone(userTimezone).toISO().split('T')[0];
};

/**
 * Formats a datetime string considering user timezone preferences
 * @param {string} dateTimeString - ISO datetime string
 * @param {string} timezone - User timezone preference
 * @returns {object} Object with formatted date and time
 */
export const formatDateTimeInTimezone = (dateTimeString, timezone) => {
  if (!dateTimeString) {
    return { date: 'No especificado', time: 'No especificado' };
  }

  try {
    const userTimezone = timezone || "America/Mexico_City";
    const dt = DateTime.fromISO(dateTimeString).setZone(userTimezone);
    
    if (!dt.isValid) {
      return { date: 'No especificado', time: 'No especificado' };
    }

    const date = dt.toFormat('dd-MM-yyyy');
    const time = dt.toFormat('HH:mm');
    
    return { date, time };
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return { date: 'No especificado', time: 'No especificado' };
  }
};

/**
 * Creates a Date object from ISO string considering user timezone
 * @param {string} dateTimeString - ISO datetime string  
 * @param {string} timezone - User timezone preference
 * @returns {Date} Date object adjusted for timezone
 */
export const createDateFromTimezone = (dateTimeString, timezone) => {
  if (!dateTimeString) return null;
  
  try {
    const userTimezone = timezone || "America/Mexico_City";
    const dt = DateTime.fromISO(dateTimeString).setZone(userTimezone);
    
    if (!dt.isValid) return null;
    
    return dt.toJSDate();
  } catch (error) {
    console.error('Error creating date from timezone:', error);
    return null;
  }
};